import React, { useCallback } from "react";
import { useSalon } from "../../context/SalonContext";
import { SalonContextType } from "../../types/context";
import usePrintDocument from "../../hooks/usePrintDocument";
import { ReceiptTransaction } from "../../utils/receiptModel";
import TransactionReceipt from "./TransactionReceipt";

interface UseReceiptPrint {
  /** Print the order receipt for one transaction — mounts a single hidden
   *  receipt and opens the browser/OS print dialog once it (and the logo) are
   *  ready. */
  printReceipt: (transaction: ReceiptTransaction) => void;
  /** Render this ONCE at the surface root. It is the single hidden `.printOnly`
   *  receipt, mounted only while a print is pending. */
  receipt: React.ReactNode;
}

/**
 * Reusable transaction-receipt printing for every surface (Sales Transactions /
 * Payments drawers, calendar appointment drawer, mobile appointment sheet).
 * Business identity (name, logo, timezone) is injected from `SalonContext`.
 * Instantiate it ONCE per surface, render `receipt` a single time, and call
 * `printReceipt(transaction)` to print.
 */
export default function useReceiptPrint(): UseReceiptPrint {
  const { businessInfo } = useSalon() as SalonContextType;

  const renderReceipt = useCallback(
    (transaction: ReceiptTransaction) => (
      <TransactionReceipt
        transaction={transaction}
        businessInfo={businessInfo ?? null}
      />
    ),
    [businessInfo],
  );

  const { print, printNode } = usePrintDocument(renderReceipt, {
    waitForImageUrl: businessInfo?.logo_url || null,
  });

  return { printReceipt: print, receipt: printNode };
}
